import React from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RootState } from '@/redux/store';
import { useToast } from '@/components/ui/use-toast';
import { useGetDashboardConfigQuery, useUpdateDashboardConfigMutation } from '@/services/dashboardConfigApi';
import {
  FacilityCoachConfigFormData,
  facilityCoachConfigSchema,
  defaultFacilityCoachConfigValues,
} from '@/types/dashboard-config';
import ConfigCard from './components/ConfigCard';
import NumberInput from './components/NumberInput';
import { ThresholdGroup } from './components/ThresholdGroup';
import { LoadingButton } from '@/components/ui/common/loading-button';
import { ErrorType } from '@/types';

interface FacilityCoachSectionProps {
  refetch: () => void;
}

export default function FacilityCoachSection({ refetch }: FacilityCoachSectionProps) {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { toast } = useToast();

  const [updateConfig, { isLoading }] = useUpdateDashboardConfigMutation();
  const { data: configData } = useGetDashboardConfigQuery({ orgId });

  const form = useForm<FacilityCoachConfigFormData>({
    mode: 'all',
    resolver: zodResolver(facilityCoachConfigSchema),
    defaultValues: defaultFacilityCoachConfigValues,
  });

  const { handleSubmit, formState: { errors }, reset, setValue, watch } = form;
  const [isEditing, setIsEditing] = React.useState(false);

  const resetToLoaded = React.useCallback(() => {
    if (configData?.dashboard_configuration) {
      const config = configData.dashboard_configuration;
      reset({
        facility_coach_low_utilisation_threshold: config.facility_coach_low_utilisation_threshold || '',
        facility_coach_facility_utilisation_red_cutoff: config.facility_coach_facility_utilisation_red_cutoff || '',
        facility_coach_facility_utilisation_amber_cutoff: config.facility_coach_facility_utilisation_amber_cutoff || '',
        facility_coach_facility_utilisation_green_cutoff: config.facility_coach_facility_utilisation_green_cutoff || '',
        facility_coach_coach_utilisation_red_cutoff: config.facility_coach_coach_utilisation_red_cutoff || '',
        facility_coach_coach_utilisation_amber_cutoff: config.facility_coach_coach_utilisation_amber_cutoff || '',
        facility_coach_coach_utilisation_green_cutoff: config.facility_coach_coach_utilisation_green_cutoff || '',
        facility_coach_retention_red_cutoff: config.facility_coach_retention_red_cutoff || '',
        facility_coach_retention_amber_cutoff: config.facility_coach_retention_amber_cutoff || '',
        facility_coach_retention_green_cutoff: config.facility_coach_retention_green_cutoff || '',
      });
    }
  }, [configData, reset]);

  React.useEffect(() => {
    resetToLoaded();
  }, [resetToLoaded]);

  const handleCancelEdit = React.useCallback(() => {
    setIsEditing(false);
    resetToLoaded();
  }, [resetToLoaded]);

  const onSubmit = async (formData: FacilityCoachConfigFormData) => {
    try {
      const config: Record<string, string> = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          config[key] = String(value);
        }
      });

      await updateConfig({ dashboard_configuration: config }).unwrap();
      toast({ variant: 'success', title: 'Facility & Coach settings saved successfully' });
      setIsEditing(false);
      refetch();
    } catch (error) {
      const typedError = error as ErrorType;
      toast({
        variant: 'destructive',
        title: 'Error',
        description: typedError.data?.detail || typedError.data?.message || 'Failed to update facility & coach settings',
      });
    }
  };

  const onError = (fieldErrors: any) => {
    console.error('Facility & Coach form validation errors:', fieldErrors);
    toast({ variant: 'destructive', description: 'Please check the entered values and fix any validation errors' });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} className="flex flex-col gap-4">
      {/* Low utilisation threshold */}
      <ConfigCard
        title="Low utilisation threshold"
        description="Facilities at or below this appear in the low-utilisation alert."
      >
        <div className="max-w-xs">
          {isEditing ? (
            <NumberInput
              aria-label="Low utilisation threshold"
              value={String(watch('facility_coach_low_utilisation_threshold') || '')}
              onChange={(value) => setValue('facility_coach_low_utilisation_threshold', value, { shouldValidate: true, shouldDirty: true })}
              unit="%"
              placeholder="Low utilisation threshold"
              min={1}
              max={100}
              error={errors.facility_coach_low_utilisation_threshold?.message}
            />
          ) : (
            <div className="flex min-h-[38px] items-center justify-end rounded-lg border border-border bg-muted/40 px-2.5 py-2 text-[13px] text-foreground">
              {String(watch('facility_coach_low_utilisation_threshold') || 'Not set')}
            </div>
          )}
        </div>
      </ConfigCard>

      {/* Utilisation badge colors */}
      <ConfigCard
        title="Utilisation badge colors"
        description="Facility performance table. Cutoffs must increase from red to green."
      >
        <ThresholdGroup
          prefixAriaLabel="Facility utilisation"
          redValue={String(watch('facility_coach_facility_utilisation_red_cutoff') || '')}
          amberValue={String(watch('facility_coach_facility_utilisation_amber_cutoff') || '')}
          greenValue={String(watch('facility_coach_facility_utilisation_green_cutoff') || '')}
          onRedChange={(value) => setValue('facility_coach_facility_utilisation_red_cutoff', value, { shouldValidate: true, shouldDirty: true })}
          onAmberChange={(value) => setValue('facility_coach_facility_utilisation_amber_cutoff', value, { shouldValidate: true, shouldDirty: true })}
          onGreenChange={(value) => setValue('facility_coach_facility_utilisation_green_cutoff', value, { shouldValidate: true, shouldDirty: true })}
          errors={{
            red: errors.facility_coach_facility_utilisation_red_cutoff?.message,
            amber: errors.facility_coach_facility_utilisation_amber_cutoff?.message,
            green: errors.facility_coach_facility_utilisation_green_cutoff?.message,
          }}
          isEditing={isEditing}
        />
      </ConfigCard>

      {/* Coach utilisation badge colors */}
      <ConfigCard
        title="Coach utilisation badge colors"
        description="Coach performance table. Cutoffs must increase from red to green."
      >
        <ThresholdGroup
          prefixAriaLabel="Coach utilisation"
          redValue={String(watch('facility_coach_coach_utilisation_red_cutoff') || '')}
          amberValue={String(watch('facility_coach_coach_utilisation_amber_cutoff') || '')}
          greenValue={String(watch('facility_coach_coach_utilisation_green_cutoff') || '')}
          onRedChange={(value) => setValue('facility_coach_coach_utilisation_red_cutoff', value, { shouldValidate: true, shouldDirty: true })}
          onAmberChange={(value) => setValue('facility_coach_coach_utilisation_amber_cutoff', value, { shouldValidate: true, shouldDirty: true })}
          onGreenChange={(value) => setValue('facility_coach_coach_utilisation_green_cutoff', value, { shouldValidate: true, shouldDirty: true })}
          errors={{
            red: errors.facility_coach_coach_utilisation_red_cutoff?.message,
            amber: errors.facility_coach_coach_utilisation_amber_cutoff?.message,
            green: errors.facility_coach_coach_utilisation_green_cutoff?.message,
          }}
          isEditing={isEditing}
        />
      </ConfigCard>

      {/* Retention badge colors */}
      <ConfigCard
        title="Retention badge colors"
        description="Retention column on the coach performance table. Cutoffs must increase from red to green."
      >
        <ThresholdGroup
          prefixAriaLabel="Retention"
          redValue={String(watch('facility_coach_retention_red_cutoff') || '')}
          amberValue={String(watch('facility_coach_retention_amber_cutoff') || '')}
          greenValue={String(watch('facility_coach_retention_green_cutoff') || '')}
          onRedChange={(value) => setValue('facility_coach_retention_red_cutoff', value, { shouldValidate: true, shouldDirty: true })}
          onAmberChange={(value) => setValue('facility_coach_retention_amber_cutoff', value, { shouldValidate: true, shouldDirty: true })}
          onGreenChange={(value) => setValue('facility_coach_retention_green_cutoff', value, { shouldValidate: true, shouldDirty: true })}
          errors={{
            red: errors.facility_coach_retention_red_cutoff?.message,
            amber: errors.facility_coach_retention_amber_cutoff?.message,
            green: errors.facility_coach_retention_green_cutoff?.message,
          }}
          isEditing={isEditing}
        />
      </ConfigCard>

      {/* Action buttons */}
      <div className="flex flex-wrap justify-end gap-2 pt-1">
        {!isEditing ? (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            disabled={isLoading}
            className="rounded-lg border border-border bg-card px-3.5 py-2 text-[13px] font-medium text-foreground transition-colors hover:bg-muted/50 disabled:opacity-50"
          >
            Edit
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={handleCancelEdit}
              disabled={isLoading}
              className="rounded-lg border border-border bg-card px-3.5 py-2 text-[13px] font-medium text-foreground transition-colors hover:bg-muted/50 disabled:opacity-50"
            >
              Cancel
            </button>

            <LoadingButton
              type="submit"
              loading={isLoading}
              disabled={isLoading}
              className="rounded-lg px-3.5 py-2 text-[13px] font-medium transition-colors"
            >
              Save changes
            </LoadingButton>
          </>
        )}
      </div>
    </form>
  );
}


